import {
  useGlobalSearchParams as useGlobalSearchParamsFromExpoRouter,
  useLocalSearchParams as useLocalSearchParamsFromExpoRouter,
} from "expo-router";
import { resolveRouteParams } from "./route-params.ts";

/**
 * Like Expo Router's `useLocalSearchParams`, but each param is passed through
 * {@link resolveRouteParam} (multi-pass percent-decoding and array flattening).
 */
export const useLocalSearchParams: typeof useLocalSearchParamsFromExpoRouter = () => {
  const params = useLocalSearchParamsFromExpoRouter();
  return resolveRouteParams(params);
};

/**
 * Use inside `_layout.tsx` files. Reads global params (so navigating between
 * sibling children that share the layout updates the value) and falls back to
 * local params on a per-key basis whenever the global value is falsy. The local
 * fallback rescues the layout when a different tab is focused: global params
 * read off the focused route and blank out, while local params still hold the
 * value captured when the layout mounted.
 *
 * Both sources are resolved via {@link resolveRouteParams}.
 */
export const useLayoutSearchParams: typeof useLocalSearchParamsFromExpoRouter = () => {
  const global = resolveRouteParams(useGlobalSearchParamsFromExpoRouter());
  const local = resolveRouteParams(useLocalSearchParamsFromExpoRouter());
  const merged: Record<string, string | string[] | undefined> = { ...local };
  for (const [key, value] of Object.entries(global)) {
    if (value) {
      merged[key] = value;
    }
  }
  return merged as ReturnType<typeof useLocalSearchParamsFromExpoRouter>;
};

export { useLocalSearchParams as useLocalSearchParamsFromExpoRouter } from "expo-router";
